import { prefersReducedMotion } from '@theme/utilities';

const MD_BREAKPOINT = 768;
const DISPLAY_DURATION = 3000; // 3 seconds
const TRANSITION_DURATION = 400; // 0.4 seconds
const TRANSITION_TIMING = 'cubic-bezier(0.65, 0, 0.35, 1)'; // cubic easing

class HeroAnimation extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.animationFrameId = null;
    this.startTime = null;
    this.isAnimating = false;
    this.childElements = [];
    this.handleResize = this.#onResize.bind(this);
  }

  connectedCallback() {
    // Only select children with data-animate attribute
    this.childElements = Array.from(this.children).filter(child => 
      child.hasAttribute('data-animate')
    );
    
    window.addEventListener('resize', this.handleResize);
    this.#checkAndInit();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    this.#stopAnimation();
  }

  #onResize() {
    this.#checkAndInit();
  }

  #checkAndInit() {
    const isMobile = window.innerWidth < MD_BREAKPOINT;

    if (isMobile && !this.isAnimating) {
      this.#init();
    } else if (!isMobile && this.isAnimating) {
      this.#stopAnimation();
      this.#resetStyles();
    }
  }

  #stopAnimation() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.isAnimating = false;
  }

  #resetStyles() {
    this.childElements.forEach((child) => {
      child.style.opacity = '';
      child.style.transform = '';
      child.style.transition = '';
      child.style.position = '';
      child.style.left = '';
      child.style.right = '';
    });
    this.style.position = '';
    this.style.minHeight = '';
  }

  #init() {
    if (this.childElements.length === 0) return;

    this.isAnimating = true;

    // Hide all children initially
    this.childElements.forEach((child) => {
      child.style.opacity = '0';
      child.style.transform = 'translateY(20px)';
      child.style.transition = `opacity ${TRANSITION_DURATION}ms ${TRANSITION_TIMING}, transform ${TRANSITION_DURATION}ms ${TRANSITION_TIMING}`;
      child.style.position = 'absolute';
      child.style.left = '0';
      child.style.right = '0';
    });

    // Set container to relative positioning
    this.style.position = 'relative';
    this.style.minHeight = '40px';

    this.#startAnimation();
  }

  #startAnimation() {
    if (!this.isAnimating) return;
    
    const currentChild = this.childElements[this.currentIndex];
    
    // Show current item (scroll down from top)
    currentChild.style.opacity = '1';
    currentChild.style.transform = 'translateY(0)';

    this.startTime = performance.now();
    this.#animate();
  }

  #animate() {
    if (!this.isAnimating) return;
    
    const currentTime = performance.now();
    const elapsed = currentTime - this.startTime;

    if (elapsed >= DISPLAY_DURATION) {
      this.#transition();
    } else {
      this.animationFrameId = requestAnimationFrame(() => this.#animate());
    }
  }

  #transition() {
    if (!this.isAnimating) return;
    
    const currentChild = this.childElements[this.currentIndex];
    const nextIndex = (this.currentIndex + 1) % this.childElements.length;
    const nextChild = this.childElements[nextIndex];

    // Force next item to starting position (without transition)
    nextChild.style.transition = 'none';
    nextChild.style.transform = 'translateY(20px)';
    nextChild.style.opacity = '0';
    
    // Force browser to apply the changes
    void nextChild.offsetHeight;
    
    // Re-enable transition
    nextChild.style.transition = `opacity ${TRANSITION_DURATION}ms ${TRANSITION_TIMING}, transform ${TRANSITION_DURATION}ms ${TRANSITION_TIMING}`;

    // Use requestAnimationFrame to ensure transition happens
    requestAnimationFrame(() => {
      if (!this.isAnimating) return;
      
      // Current item scrolls up, next item scrolls down simultaneously
      currentChild.style.opacity = '0';
      currentChild.style.transform = 'translateY(-20px)'; // scroll up

      nextChild.style.opacity = '1';
      nextChild.style.transform = 'translateY(0)'; // scroll down

      // Move to next index
      this.currentIndex = nextIndex;

      // After transition completes, reset and continue
      const transitionStartTime = performance.now();
      const waitForTransition = () => {
        if (!this.isAnimating) return;
        
        const transitionElapsed = performance.now() - transitionStartTime;
        
        if (transitionElapsed >= TRANSITION_DURATION) {
          currentChild.style.transform = 'translateY(20px)';
          this.#startAnimation();
        } else {
          this.animationFrameId = requestAnimationFrame(waitForTransition);
        }
      };
      
      this.animationFrameId = requestAnimationFrame(waitForTransition);
    });
  }
}

if (!customElements.get('hero-animation')) {
  customElements.define('hero-animation', HeroAnimation);
}
